import { useEffect, useMemo, useState, FormEvent, SyntheticEvent } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    List,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Add as AddIcon,
    Save as SaveIcon,
    Edit as EditIcon,
    EventNote as TemplateSetIcon,
} from '@mui/icons-material';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    verticalListSortingStrategy,
    arrayMove,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import SortableTemplateRow from './schedule/SortableTemplateRow';
import BaseModal from '@/components/modals/BaseModal';
import ScheduleTemplatesDialog from '@/components/dialogs/ScheduleTemplatesDialog.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { close } from '@/store/uiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import {
    useGetUserInfoQuery,
    useGetScheduleTemplatesQuery,
    useGetScheduleTemplateSetsQuery,
    useCreateScheduleTemplateSetMutation,
    useUpdateScheduleTemplateSetMutation,
    useDeleteScheduleTemplateSetMutation,
} from '@/store/apiSlice';
import type { ScheduleTemplate } from '@/types';
import { splitTags } from '@/utils/tags';

type TemplateRef = { id: number; position?: number };
type LocalTemplateSet = {
    id: number;
    name: string;
    templates?: TemplateRef[];
};

type DraftState = {
    selectedTemplateIds: string[];
    draftName: string;
};

const ScheduleTemplatesModal = () => {
    const dispatch = useAppDispatch();
    const { LL } = useI18nContext();

    const openModal = useAppSelector((state) => state.ui.open.scheduleTemplateSets);
    const onClose = () => dispatch(close('scheduleTemplateSets'));

    const { data: userInfo } = useGetUserInfoQuery();
    const canEdit = !!(userInfo?.admin || userInfo?.scheduleTemplateEditor);

    const {
        data: templates = [],
        isLoading: templatesLoading,
        error: templatesError,
    } = useGetScheduleTemplatesQuery(undefined, { skip: !openModal });

    const {
        data: sets = [],
        isLoading: setsLoading,
        error: setsError,
    } = useGetScheduleTemplateSetsQuery(undefined, { skip: !openModal });

    // Keep a local, stable copy of sets to prevent UI flicker/shrinking while mutations refetch.
    const [displaySets, setDisplaySets] = useState<LocalTemplateSet[]>([]);

    useEffect(() => {
        if (!openModal) return;
        // Normalize API data into our minimal shape.
        setDisplaySets(
            (sets ?? []).map((s: any) => ({
                id: s.id,
                name: s.name,
                templates: (s.templates ?? []).map((t: any) => ({
                    id: t.id,
                    position: t.position,
                })),
            }))
        );
    }, [openModal, sets]);

    const [createSet, createState] = useCreateScheduleTemplateSetMutation();
    const [updateSet, updateState] = useUpdateScheduleTemplateSetMutation();
    const [deleteSet] = useDeleteScheduleTemplateSetMutation();

    const [activeTabIndex, setActiveTabIndex] = useState(0);

    // Draft set state: either editing an existing set or creating a new one
    const [isCreating, setIsCreating] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
    const [addTemplateValue, setAddTemplateValue] = useState<ScheduleTemplate | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Creating flow (name first)
    const [createNameOpen, setCreateNameOpen] = useState(false);
    const [createNameValue, setCreateNameValue] = useState('');
    const [createNameError, setCreateNameError] = useState<string | null>(null);

    // Renaming flow
    const [editOpen, setEditOpen] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const [renameError, setRenameError] = useState<string | null>(null);

    // Deleting confirm flow
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    // Manage schedule templates dialog
    const [templatesManagerOpen, setTemplatesManagerOpen] = useState(false);

    const saving = createState.isLoading || updateState.isLoading;

    // Only show the modal loading overlay while initially fetching data.
    // During save mutations we keep the content mounted to avoid flicker/shrinking.
    const isBusy = (templatesLoading || setsLoading) && !saving;

    // Stable reference to the currently displayed set (avoid nulling out during refetch).
    const activeSet = useMemo(() => {
        if (isCreating) return null;
        return displaySets[activeTabIndex] ?? null;
    }, [displaySets, activeTabIndex, isCreating]);

    const createInstanceId = (templateId: number) => {
        try {
            const uuid = globalThis.crypto?.randomUUID?.();
            return `${templateId}-${uuid ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;
        } catch {
            return `${templateId}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
        }
    };

    const extractTemplateId = (instanceId: string) => {
        const idStr = String(instanceId).split('-')[0];
        return parseInt(idStr, 10);
    };

    // Temp ids for unsaved draft tabs (negative numbers to avoid clashing with DB ids)
    const createTempSetId = () => -Math.floor(Date.now() + Math.random() * 1000);

    useEffect(() => {
        if (!openModal) return;
        setError(null);
        setIsCreating(false);
        setActiveTabIndex(0);
    }, [openModal]);

    // When closing the modal, drop any unsaved draft tab
    useEffect(() => {
        if (openModal) return;
        setDisplaySets((prev) => prev.filter((s) => s.id > 0));
    }, [openModal]);

    const [draftsBySetId, setDraftsBySetId] = useState<Record<string, DraftState>>({});

    const storageKey = 'scheduleTemplateSets.draftsBySetId';

    // Load cached drafts from sessionStorage on open
    useEffect(() => {
        if (!openModal) return;
        try {
            const raw = sessionStorage.getItem(storageKey);
            if (raw) {
                const parsed = JSON.parse(raw) as Record<string, DraftState>;
                setDraftsBySetId(parsed ?? {});
            }
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal]);

    useEffect(() => {
        if (!openModal) return;
        try {
            sessionStorage.setItem(storageKey, JSON.stringify(draftsBySetId));
        } catch {
            // ignore
        }
    }, [openModal, draftsBySetId]);

    // Clear stored drafts on close
    useEffect(() => {
        if (openModal) return;
        try {
            sessionStorage.removeItem(storageKey);
        } catch {
            // ignore
        }
    }, [openModal]);

    const getDraftKey = (setId: number) => String(setId);

    const saveCurrentDraft = () => {
        const set = displaySets[activeTabIndex];
        if (!set) return;

        setDraftsBySetId((prev) => ({
            ...prev,
            [getDraftKey(set.id)]: {
                selectedTemplateIds,
                draftName,
            },
        }));
    };

    const restoreDraftFor = (set: LocalTemplateSet) => {
        const draft = draftsBySetId[getDraftKey(set.id)];
        if (draft) {
            setDraftName(draft.draftName);
            setSelectedTemplateIds(draft.selectedTemplateIds);
            return true;
        }
        return false;
    };

    // When the active set changes, load it into the draft.
    useEffect(() => {
        if (!openModal) return;

        const set = displaySets[activeTabIndex];
        if (!set) {
            setDraftName('');
            setSelectedTemplateIds([]);
            return;
        }

        // Prefer local cached draft (keeps unsaved edits across tab changes)
        if (restoreDraftFor(set)) return;

        // Otherwise derive from set data
        setDraftName(set.name);
        setSelectedTemplateIds(
            [...(set.templates ?? [])]
                .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
                .map((t) => createInstanceId(t.id))
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openModal, displaySets, activeTabIndex]);

    const templateById = useMemo(() => {
        const map = new Map<number, ScheduleTemplate>();
        for (const t of templates) map.set(t.id, t);
        return map;
    }, [templates]);

    const templateLabelWithTags = useMemo(
        () => (t: ScheduleTemplate) => {
            const tags = splitTags(t.tags);
            return tags.length ? `${tags.join(', ')} - ${t.template}` : t.template;
        },
        []
    );

    const availableTemplates = useMemo(() => templates, [templates]);

    // Use the same sensor setup as the schedule modal (better mobile behavior).
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const oldIndex = selectedTemplateIds.indexOf(active.id as string);
        const newIndex = selectedTemplateIds.indexOf(over.id as string);
        if (oldIndex === -1 || newIndex === -1) return;

        setSelectedTemplateIds((prev) => arrayMove(prev, oldIndex, newIndex));
    };

    const handleAddTemplate = (template: ScheduleTemplate) => {
        if (!canEdit) return;
        setSelectedTemplateIds((prev) => [...prev, createInstanceId(template.id)]);
        setAddTemplateValue(null);
    };

    const handleRemoveTemplate = (instanceId: string) => {
        if (!canEdit) return;
        setSelectedTemplateIds((prev) => prev.filter((id) => id !== instanceId));
    };

    const startCreate = () => {
        if (!canEdit) return;
        setError(null);
        setCreateNameValue('');
        setCreateNameOpen(true);
    };

    const confirmCreateName = (e?: FormEvent) => {
        e?.preventDefault();
        if (!canEdit) return;

        const name = createNameValue.trim();
        if (!name) {
            setCreateNameError(LL.scheduleTemplateSets.pleaseEnterName());
            return;
        }

        // Prevent duplicate names (case-insensitive)
        const exists = displaySets.some((s) => s.name.toLowerCase() === name.toLowerCase());
        if (exists) {
            setCreateNameError(LL.scheduleTemplateSets.nameExists());
            return;
        }

        const newIndex = displaySets.length;
        const tempId = createTempSetId();

        // Add the tab immediately as a local draft entry
        setDisplaySets((prev) => [
            ...prev,
            {
                id: tempId,
                name,
                templates: [],
            },
        ]);

        setActiveTabIndex(newIndex);
        setIsCreating(true);
        setDraftName(name);
        setSelectedTemplateIds([]);
        setAddTemplateValue(null);

        // seed draft cache for this new set
        setDraftsBySetId((prev) => ({
            ...prev,
            [String(tempId)]: { selectedTemplateIds: [], draftName: name },
        }));

        setCreateNameOpen(false);
    };

    const cancelCreateName = () => {
        setCreateNameOpen(false);
        setCreateNameError(null);
    };

    const confirmRename = async (e?: FormEvent) => {
        e?.preventDefault();
        if (!canEdit || !activeSet || isCreating) return;

        const name = renameValue.trim();
        if (!name) {
            setRenameError(LL.scheduleTemplateSets.pleaseEnterName());
            return;
        }

        // Prevent duplicate names (case-insensitive) excluding current set
        const exists = displaySets.some(
            (s, idx) => idx !== activeTabIndex && s.name.toLowerCase() === name.toLowerCase()
        );
        if (exists) {
            setRenameError(LL.scheduleTemplateSets.nameExists());
            return;
        }

        try {
            await updateSet({ id: activeSet.id, name, templateIds: templateIdsForSave }).unwrap();
            setEditOpen(false);
        } catch {
            setError(LL.scheduleTemplateSets.failedToRename());
        }
    };

    const cancelRename = () => {
        setEditOpen(false);
        setRenameError(null);
    };

    const openRenameDialogFor = (setIndex: number) => {
        const s = displaySets[setIndex];
        if (!canEdit || !s || isCreating) return;
        setError(null);
        setActiveTabIndex(setIndex);
        setRenameValue(s.name);
        setRenameError(null);
        setEditOpen(true);
    };

    const openDeleteConfirm = () => {
        if (!canEdit || !activeSet || isCreating) return;
        setError(null);
        setDeleteConfirmOpen(true);
    };

    const cancelDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
    };

    const confirmDelete = async () => {
        if (!canEdit || !activeSet || isCreating) return;

        // Local-only draft sets have negative ids; delete them locally without API call.
        if (activeSet.id < 0) {
            const deletedId = activeSet.id;
            setDisplaySets((prev) => prev.filter((s) => s.id !== deletedId));
            setDeleteConfirmOpen(false);
            setActiveTabIndex(0);

            // Remove local caches for deleted draft set
            setDraftsBySetId((prev) => {
                const next = { ...prev };
                delete next[String(deletedId)];
                return next;
            });

            setEditOpen(false);
            return;
        }

        try {
            const deletedId = activeSet.id;
            await deleteSet(deletedId).unwrap();
            setDeleteConfirmOpen(false);
            setActiveTabIndex(0);

            // Remove local caches for deleted set
            setDraftsBySetId((prev) => {
                const next = { ...prev };
                delete next[String(deletedId)];
                return next;
            });

            setEditOpen(false);
        } catch {
            setError(LL.scheduleTemplateSets.failedToDelete());
        }
    };

    // Before handleSave, derive the numeric ids we send to the API (preserving duplicates)
    const templateIdsForSave = useMemo(
        () => selectedTemplateIds.map((instanceId) => extractTemplateId(instanceId)),
        [selectedTemplateIds]
    );

    // Save now persists ONLY template membership for the currently selected set (or creates a new set when in create mode)
    const handleSave = async () => {
        setError(null);
        if (!canEdit || saving) return;

        const name = draftName.trim();
        if (!name) {
            setError(LL.scheduleTemplateSets.pleaseEnterName());
            return;
        }

        try {
            if (isCreating) {
                const tempId = displaySets[activeTabIndex]?.id;
                const result = await createSet({ name, templateIds: templateIdsForSave }).unwrap();

                setDisplaySets((prev) =>
                    prev.map((s, idx) =>
                        idx !== activeTabIndex
                            ? s
                            : {
                                  id: result.id,
                                  name,
                                  templates: templateIdsForSave.map((id, position) => ({
                                      id,
                                      position,
                                  })),
                              }
                    )
                );

                // move draft cache key from temp id -> real id
                if (typeof tempId === 'number') {
                    setDraftsBySetId((prev) => {
                        const next = { ...prev };
                        const tempKey = String(tempId);
                        const draft = next[tempKey];
                        if (draft) {
                            delete next[tempKey];
                            next[String(result.id)] = draft;
                        }
                        return next;
                    });
                }

                setIsCreating(false);
            } else if (activeSet) {
                await updateSet({
                    id: activeSet.id,
                    name: activeSet.name,
                    templateIds: templateIdsForSave,
                }).unwrap();

                setDisplaySets((prev) =>
                    prev.map((s, idx) =>
                        idx !== activeTabIndex
                            ? s
                            : {
                                  ...s,
                                  templates: templateIdsForSave.map((id, position) => ({
                                      id,
                                      position,
                                  })),
                              }
                    )
                );
            }

            saveCurrentDraft();
            onClose();
        } catch {
            setError(LL.scheduleTemplateSets.failedToSave());
        }
    };

    // Handle selecting an existing set tab or the trailing "New set" tab
    const handleTabChange = (_: SyntheticEvent, newValue: number) => {
        // Persist current draft before switching
        saveCurrentDraft();

        // Clicking the "New set" tab triggers the name dialog
        if (canEdit && newValue === displaySets.length && !createNameOpen) {
            startCreate();
            return;
        }

        setIsCreating(false);
        setActiveTabIndex(newValue);
        setError(null);
    };

    const tabValue =
        canEdit && createNameOpen
            ? displaySets.length
            : displaySets.length === 0
              ? canEdit
                  ? displaySets.length
                  : 0
              : Math.min(activeTabIndex, displaySets.length - 1);

    if (!openModal) return null;

    return (
        <>
            <BaseModal
                open={openModal}
                onClose={onClose}
                maxWidth="lg"
                styledHeader={{
                    icon: <TemplateSetIcon />,
                    title: LL.menu.scheduleTemplates(),
                    subtitle: [
                        canEdit
                            ? LL.scheduleTemplateSets.headerSubtitleEdit()
                            : LL.scheduleTemplateSets.headerSubtitleView(),
                    ],
                    gradient: 'linear-gradient(135deg, #7f53ac 0%, #647dee 100%)',
                }}
                loading={isBusy}
                actions={
                    (isCreating || activeSet) && (
                        <>
                            <Button onClick={onClose} disabled={saving}>
                                {LL.common.close()}
                            </Button>
                            {canEdit && (
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSave}
                                    disabled={saving}
                                >
                                    {LL.common.save()}
                                </Button>
                            )}
                        </>
                    )
                }
            >
                <Box
                    sx={{
                        // Reduce padding on very small screens to show more content.
                        p: { xs: 1, sm: 3 },
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    {!canEdit && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            {LL.errors.unauthorized()}
                        </Alert>
                    )}

                    {(templatesError || setsError) && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {LL.errors.failedToLoad()}
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={{ xs: 1, md: 2 }}
                        sx={{ mb: { xs: 1, md: 2 } }}
                    >
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ flex: 1, minHeight: { xs: 40, sm: 44 } }}
                        >
                            {displaySets.map((s, idx) => (
                                <Tab
                                    key={s.id}
                                    value={idx}
                                    label={
                                        <Stack
                                            direction="row"
                                            sx={{ alignItems: 'center', gap: 0.5, maxWidth: 220 }}
                                        >
                                            <Typography variant="body2" noWrap>
                                                {s.name}
                                            </Typography>

                                            {canEdit && (
                                                <>
                                                    <Tooltip
                                                        title={LL.scheduleTemplateSets.rename()}
                                                    >
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                openRenameDialogFor(idx);
                                                            }}
                                                            sx={{ ml: 0.25 }}
                                                        >
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Stack>
                                    }
                                />
                            ))}

                            {canEdit && (
                                <Tab
                                    key="new-set"
                                    value={displaySets.length}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        startCreate();
                                    }}
                                    label={
                                        <Stack
                                            direction="row"
                                            sx={{ alignItems: 'center', gap: 0.5 }}
                                        >
                                            <AddIcon fontSize="small" />
                                            <Typography variant="body2" noWrap>
                                                {LL.scheduleTemplateSets.newSetTab()}
                                            </Typography>
                                        </Stack>
                                    }
                                />
                            )}
                        </Tabs>
                    </Stack>

                    {/* Content area */}
                    <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                        {(isCreating || activeSet) && (
                            <>
                                <Stack
                                    direction="row"
                                    spacing={{ xs: 0.75, sm: 1 }}
                                    sx={{ mb: { xs: 1, sm: 2 }, alignItems: 'center' }}
                                >
                                    <Autocomplete
                                        value={addTemplateValue}
                                        onChange={(_, value) => {
                                            if (value) handleAddTemplate(value);
                                        }}
                                        options={availableTemplates}
                                        getOptionLabel={(option) => templateLabelWithTags(option)}
                                        disabled={!canEdit || saving}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={LL.scheduleTemplateSets.addTemplateLabel()}
                                                placeholder={LL.scheduleTemplateSets.addTemplatePlaceholder()}
                                                size="small"
                                            />
                                        )}
                                        sx={{ flex: 1 }}
                                    />

                                    {canEdit && (
                                        <Tooltip title={LL.adminScheduleTemplatesPage.title()}>
                                            <span>
                                                <IconButton
                                                    onClick={() => setTemplatesManagerOpen(true)}
                                                    disabled={saving}
                                                    size="small"
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </Stack>

                                <Divider sx={{ mb: { xs: 1, sm: 2 } }} />

                                <Typography
                                    variant="subtitle1"
                                    fontWeight={700}
                                    sx={{ mb: { xs: 0.75, sm: 1 } }}
                                >
                                    {LL.scheduleTemplateSets.templatesInSetTitle()}
                                </Typography>

                                <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={handleDragEnd}
                                >
                                    <SortableContext
                                        items={selectedTemplateIds}
                                        strategy={verticalListSortingStrategy}
                                    >
                                        <List
                                            sx={{
                                                minHeight: 80,
                                                overflowX: 'hidden',
                                                // Helps on mobile: allow vertical panning but still permit dragging.
                                                touchAction: 'pan-y',
                                            }}
                                        >
                                            {selectedTemplateIds.map((instanceId) => {
                                                const template = templateById.get(
                                                    extractTemplateId(instanceId)
                                                );
                                                if (!template) return null;

                                                return (
                                                    <SortableTemplateRow
                                                        key={instanceId}
                                                        instanceId={instanceId}
                                                        template={template}
                                                        onRemove={() =>
                                                            handleRemoveTemplate(instanceId)
                                                        }
                                                        disabled={!canEdit || saving}
                                                    />
                                                );
                                            })}
                                        </List>
                                    </SortableContext>
                                </DndContext>

                                {selectedTemplateIds.length === 0 && (
                                    <Alert severity="info">
                                        {LL.scheduleTemplateSets.noTemplatesAdded()}
                                    </Alert>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </BaseModal>

            <ScheduleTemplatesDialog
                open={templatesManagerOpen}
                onClose={() => setTemplatesManagerOpen(false)}
                canEdit={canEdit}
            />

            {/* Create new set: name prompt */}
            <Dialog open={createNameOpen} onClose={cancelCreateName} maxWidth="xs" fullWidth>
                <form onSubmit={(e) => confirmCreateName(e)}>
                    <DialogTitle>{LL.scheduleTemplateSets.newSetDialogTitle()}</DialogTitle>
                    <DialogContent>
                        <TextField
                            label={LL.scheduleTemplateSets.setNameLabel()}
                            value={createNameValue}
                            onChange={(e) => {
                                setCreateNameValue(e.target.value);
                                if (createNameError) setCreateNameError(null);
                                if (error) setError(null);
                            }}
                            fullWidth
                            autoFocus
                            required
                            error={!!createNameError}
                            helperText={createNameError ?? undefined}
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={cancelCreateName}>{LL.common.cancel()}</Button>
                        <Button type="submit" variant="contained">
                            {LL.common.create()}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Rename set */}
            <Dialog open={editOpen} onClose={cancelRename} maxWidth="xs" fullWidth>
                <form onSubmit={(e) => confirmRename(e)}>
                    <DialogTitle>{LL.scheduleTemplateSets.renameDialogTitle()}</DialogTitle>
                    <DialogContent>
                        <TextField
                            label={LL.scheduleTemplateSets.newNameLabel()}
                            value={renameValue}
                            onChange={(e) => {
                                setRenameValue(e.target.value);
                                if (renameError) setRenameError(null);
                                if (error) setError(null);
                            }}
                            fullWidth
                            autoFocus
                            required
                            error={!!renameError}
                            helperText={renameError ?? undefined}
                            sx={{ mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button
                            color="error"
                            variant="outlined"
                            onClick={openDeleteConfirm}
                            disabled={saving}
                        >
                            {LL.common.delete()}
                        </Button>
                        <Button onClick={cancelRename}>{LL.common.cancel()}</Button>
                        <Button type="submit" variant="contained" disabled={saving}>
                            {LL.common.save()}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            {/* Delete confirmation */}
            <Dialog open={deleteConfirmOpen} onClose={cancelDeleteConfirm} maxWidth="xs" fullWidth>
                <DialogTitle>{LL.scheduleTemplateSets.deleteConfirmTitle()}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {LL.scheduleTemplateSets.deleteConfirmMessage()}
                    </Typography>

                    {/* Trigger delete from within (the) dialog */}
                    <Stack direction="row" justifyContent="flex-end" spacing={2}>
                        <Button onClick={cancelDeleteConfirm} disabled={saving}>
                            {LL.common.cancel()}
                        </Button>
                        <Button
                            onClick={confirmDelete}
                            variant="contained"
                            color="error"
                            disabled={saving}
                        >
                            {LL.common.delete()}
                        </Button>
                    </Stack>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ScheduleTemplatesModal;
